import React from "react"
import Footer from "../components/footer"
import Navbar from "../components/nav"

function FastingSensePolicy() {
  return (
    <>
      <header className="header-area">
        <div className="navbar-area">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <Navbar />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div id="home">
        <div className="container mt-125 mb-50">
          <div className="card-body shadow">
            <h4 id="privacy-policy-for-fastingsense-fasting-tracker">Privacy Policy for FastingSense: Fasting Tracker</h4>
            <br />
            <p><strong>Last Updated: January 28, 2026</strong></p>
            <p><strong>Version: 2.0 - Comprehensive Disclosure</strong></p>
            <br />
            <p><strong>Developer:</strong> MWX Apps</p>
            <p><strong>App Name:</strong> FastingSense: Fasting Tracker</p>
            <p><strong>Package Name:</strong> mwxapps.fastingsense.app</p>
            <p><strong>Contact Email:</strong> web-developer@outlook.com</p>
            <br />
            <hr />
            <br />
            <h5 id="table-of-contents">Table of Contents</h5>
            <br />
            <ol>
              <li><a href="#introduction">Introduction</a></li>
              <li><a href="#1-health-data-we-collect-and-access">Health Data We Collect and Access</a></li>
              <li><a href="#3-how-we-use-your-health-data">How We Use Your Health Data</a></li>
              <li><a href="#4-data-storage-and-security">Data Storage and Security</a></li>
              <li><a href="#5-data-sharing-and-third-party-services">Data Sharing and Third-Party Services</a></li>
              <li><a href="#6-permissions-requested">Permissions Requested</a></li>
              <li><a href="#7-your-rights-and-data-control">Your Rights and Data Control</a></li>
              <li><a href="#8-childrens-privacy">Children's Privacy</a></li>
              <li><a href="#9-international-users-and-data-location">International Users and Data Location</a></li>
              <li><a href="#10-changes-to-this-privacy-policy">Changes to This Privacy Policy</a></li>
              <li><a href="#11-security-measures">Security Measures</a></li>
              <li><a href="#12-medical-disclaimer">Medical Disclaimer</a></li>
              <li><a href="#17-contact-information">Contact Information</a></li>
            </ol>
            <br />
            <hr />
            <br />
            <h5 id="introduction">Introduction</h5>
            <br />
            <p>MWX Apps ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains in comprehensive detail how FastingSense ("the App") accesses, collects, uses, stores, and shares your personal information, including sensitive health and fitness data.</p>
            <br />
            <p><strong>Your privacy is our priority.</strong> FastingSense is designed with a privacy-first, local-storage approach. All your health and personal data is stored locally on your device and is never transmitted to our servers or third parties, except as explicitly described in this policy.</p>
            <br />
            <p>This privacy policy applies to all users of FastingSense and covers all data collection, processing, and sharing practices within the app.</p>
            <br />
            <hr />
            <br />
            <h5 id="1-health-data-we-collect-and-access">1. Health Data We Collect and Access</h5>
            <br />
            <p>FastingSense is a health and wellness application that collects and processes sensitive health-related information to provide you with intermittent fasting tracking, nutrition monitoring, sleep analysis, and comprehensive wellness insights. Below is a <strong>complete and detailed disclosure</strong> of all health data types we collect:</p>
            <br />
            <h6 id="11-intermittent-fasting-data">1.1 Intermittent Fasting Data</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Fasting session records: Start time, end time, duration (in hours and minutes)</li>
              <li>Fasting plans: Selected fasting protocol (e.g., 16:8, 18:6, 20:4, OMAD, 5:2, custom plans)</li>
              <li>Fasting status: Current state (actively fasting, eating window, idle/not fasting)</li>
              <li>Eating window settings: Preferred eating window start and end times</li>
              <li>Fasting schedule: Active days of the week for fasting (e.g., Monday-Friday only)</li>
              <li>Fasting history: Completed fasts, incomplete fasts, canceled sessions</li>
              <li>Fasting streaks: Current consecutive fasting streak, longest streak achieved, total completed fasts</li>
              <li>Fasting milestones: Progress toward metabolic zones (fat burning, ketosis, autophagy, deep ketosis)</li>
              <li>Custom fasting plans: User-created fasting schedules with custom durations</li>
              <li>Fasting challenges: Participation in daily, weekly, or monthly fasting challenges</li>
              <li>Fasting achievements: Unlocked badges and progress toward achievement thresholds</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To track your intermittent fasting progress in real-time, provide accurate fasting timers, calculate metabolic zone transitions (ketosis typically begins after 12-16 hours of fasting, autophagy after 16-24 hours), generate fasting statistics and insights, maintain motivation through streaks and achievements, and provide personalized fasting recommendations based on your history and goals.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database on your device using Android Room Persistence Library. Never transmitted to external servers.</p>
            <br />
            <p><strong>Data Tables:</strong> `fasting_sessions`, `fasting_plans`, `fasting_streak`, `achievements`, `challenges`, `challenge_completions`</p>
            <br />
            <hr />
            <br />
            <h6 id="12-nutrition-and-food-intake-data">1.2 Nutrition and Food Intake Data</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Meal logs: Individual food items and meals consumed throughout the day</li>
              <li>Meal timing: Date and time of consumption for each meal</li>
              <li>Meal types: Categorization (breakfast, lunch, dinner, snacks)</li>
              <li>Nutritional information per meal:</li>
              <li>Calories (kcal) per serving and per 100g</li>
              <li>Macronutrients: Carbohydrates (g), Protein (g), Fat (g)</li>
              <li>Micronutrients: Fiber (g), Sugar (g), Saturated Fat (g) when available</li>
              <li>Portion sizes: Amount consumed (grams, milliliters, servings, pieces, cups, etc.)</li>
              <li>Food identification:</li>
              <li>Product names (e.g., "Banana", "Chicken Breast", "Coca-Cola")</li>
              <li>Brand names (e.g., "Nestlé", "Kellogg's")</li>
              <li>Barcode numbers (UPC/EAN codes when scanned)</li>
              <li>Food source identifiers (OpenFoodFacts ID, USDA FoodData Central ID, custom meal ID)</li>
              <li>Food images: Photos you take of your meals using the in-app camera</li>
              <li>Custom meals: User-created meal entries with manually entered nutritional values</li>
              <li>Favorite meals: Meals you mark as favorites for quick logging</li>
              <li>Meal categories: User-organized categories for custom meals</li>
              <li>Daily nutritional summaries: Total calories and macros consumed per day</li>
              <li>Nutritional goals: Target daily calorie intake, target macronutrient percentages or grams</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To help you track your daily caloric and macronutrient intake during eating windows, ensure you meet personalized nutritional goals based on your basal metabolic rate (BMR) and total daily energy expenditure (TDEE), understand eating patterns and food choices, make informed dietary decisions that support your fasting regimen, identify foods that affect your energy levels or weight loss progress, and provide comprehensive nutritional reports and insights.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <ul>
              <li>Meal data: Stored locally in encrypted SQLite database (`intakes`, `custom_meals`, `favorite_meals`, `favorite_categories` tables)</li>
              <li>Food images: Stored in the app's private storage directory on your device, not in the database, accessible only to FastingSense</li>
              <li>Never transmitted externally except when you explicitly export your data</li>
            </ul>
            <br />
            <p><strong>Data Sources:</strong></p>
            <ul>
              <li>Manual entry by user</li>
              <li>Barcode scanning (product information retrieved from OpenFoodFacts API - only barcode number is transmitted)</li>
              <li>Food search (search queries sent to OpenFoodFacts or USDA FoodData Central - no personal data transmitted)</li>
              <li>Custom user-created meals</li>
            </ul>
            <br />
            <hr />
            <br />
            <h6 id="13-body-measurements-and-physical-metrics">1.3 Body Measurements and Physical Metrics</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Weight logs: Weight measurements with timestamps (kg or lbs based on your preference)</li>
              <li>Height: Height in centimeters or inches (entered during onboarding)</li>
              <li>Body Mass Index (BMI): Automatically calculated from height and current weight</li>
              <li>Weight goals: Target weight you aim to achieve</li>
              <li>Weight management objectives: Selected goal (lose weight, maintain weight, gain weight, build muscle)</li>
              <li>Weight history: Historical weight measurements over time (daily, weekly, monthly)</li>
              <li>Weight trends: Progress tracking including weight loss/gain rate, trend direction</li>
              <li>Date of birth/Age: Used for age-based metabolic rate calculations</li>
              <li>Gender: Male or Female (used for calculating Basal Metabolic Rate using Mifflin-St Jeor equation)</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To calculate personalized daily caloric requirements based on the Mifflin-St Jeor equation (most accurate BMR formula: For men: BMR = 10×weight(kg) + 6.25×height(cm) - 5×age(y) + 5; For women: BMR = 10×weight(kg) + 6.25×height(cm) - 5×age(y) - 161), track weight loss or gain progress over time, compute and display your Body Mass Index (BMI = weight(kg) / height(m)²), provide body composition insights and recommendations, adjust daily caloric goals as your weight changes, personalize fasting recommendations based on your current metrics, and visualize your progress with charts and graphs.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database on your device. Tables: `user`, `weight_logs`, `tracked_days`</p>
            <br />
            <p><strong>Retention:</strong></p>
            <p>Weight logs are retained indefinitely until you manually delete them or delete all app data. Your current height, weight, age, and gender are stored in the user profile.</p>
            <br />
            <hr />
            <br />
            <h6 id="14-sleep-data-health-connect-integration">1.4 Sleep Data (Health Connect Integration)</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Sleep sessions: Individual sleep periods with start and end times</li>
              <li>Bedtime: Time you go to bed each night</li>
              <li>Wake time: Time you wake up each morning</li>
              <li>Sleep duration: Total hours and minutes of sleep per session</li>
              <li>Sleep quality: Self-reported quality rating (Poor, Fair, Good, Excellent) or Unknown</li>
              <li>Sleep stages (when available from Health Connect or wearable devices):</li>
              <li>Light sleep duration and periods</li>
              <li>Deep sleep duration and periods</li>
              <li>REM (Rapid Eye Movement) sleep duration and periods</li>
              <li>Awake periods during sleep</li>
              <li>Out of bed periods</li>
              <li>Awake in bed periods</li>
              <li>Sleep source: Origin of the data (Manual entry in app, or synced from Google Health Connect)</li>
              <li>Sleep notes: Optional text notes you add about your sleep quality or factors affecting sleep</li>
              <li>Sleep date: Date associated with each sleep session (based on bedtime)</li>
              <li>Sleep patterns: Historical sleep data for trend analysis and correlation studies</li>
            </ul>
            <br />
            <p><strong>Health Connect Integration:</strong></p>
            <ul>
              <li><strong>READ_SLEEP Permission:</strong> If you grant permission, we read sleep session data from Google Health Connect to import sleep records from your connected fitness devices, smartwatches, or other health apps (e.g., Google Fit, Fitbit, Samsung Health, Garmin Connect, etc.). This allows automatic sleep tracking without manual entry.</li>
              <li><strong>WRITE_SLEEP Permission:</strong> When you manually log sleep sessions in FastingSense, we write this data to Google Health Connect (if you grant permission) to ensure a centralized health record across your entire health ecosystem. This enables you to view all your sleep data in one place regardless of source.</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To analyze correlations between sleep quality/duration and fasting performance, understand how rest impacts your metabolic health, energy levels, weight management, and overall wellness during fasting periods, provide holistic health insights combining fasting, nutrition, and sleep data, identify patterns such as how fasting affects your sleep quality or vice versa, offer personalized recommendations for optimal eating window timing based on your sleep schedule, and help you achieve better overall health outcomes by tracking this important wellness metric.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Tables: `sleep_sessions`, `sleep_stages`. Sleep data from Health Connect is cached locally but originates from your connected health apps/devices.</p>
            <br />
            <p><strong>User Control:</strong></p>
            <p>This integration is <strong>entirely optional</strong>. You can deny Health Connect permissions and still use all sleep tracking features through manual entry. You can revoke permissions at any time through your device Settings or the Health Connect app.</p>
            <br />
            <hr />
            <br />
            <h6 id="15-physical-activity-and-exercise-data">1.5 Physical Activity and Exercise Data</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Activity type: Specific exercise or activity performed (e.g., Walking, Running, Cycling, Swimming, Strength Training, Yoga, Pilates, Dancing, Hiking, Sports, etc.)</li>
              <li>Activity name: User-provided name or selected from 50+ predefined activities</li>
              <li>Activity duration: Start time, end time, and total minutes of activity</li>
              <li>Calories burned: Estimated energy expenditure from physical activities (calculated using MET values)</li>
              <li>Activity frequency: How often you exercise (daily, weekly patterns)</li>
              <li>Activity intensity: Light, moderate, or vigorous intensity level</li>
              <li>Activity date and time: When the activity was performed</li>
              <li>Activity history: Complete record of all logged physical activities</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To calculate your Total Daily Energy Expenditure (TDEE = BMR × Physical Activity Level multiplier + activity calories), adjust your daily caloric goals based on your activity levels to ensure adequate nutrition during eating windows, track your exercise habits and routines, understand how physical activity affects your weight loss or gain progress, provide comprehensive health insights that combine fasting, nutrition, sleep, and physical activity data, and support your overall fitness and wellness goals.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Tables: `user_activities`, `tracked_days`</p>
            <br />
            <p><strong>Activity Database:</strong></p>
            <p>The app includes a database of common physical activities with associated MET (Metabolic Equivalent of Task) values for accurate calorie burn estimation. This database is stored locally in `physical_activities.json`.</p>
            <br />
            <hr />
            <br />
            <h6 id="16-hydration-data">1.6 Hydration Data</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Water intake amount: Volume of water consumed (milliliters or ounces based on your preference)</li>
              <li>Intake timestamps: Date and time of each water log entry</li>
              <li>Daily hydration total: Total water consumed per day</li>
              <li>Daily hydration goal: Personalized water intake target (typically 2-3 liters/day, customizable)</li>
              <li>Hydration patterns: Drinking habits throughout the day and across weeks</li>
              <li>Hydration reminders: User preferences for hydration reminder notifications</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To help you stay adequately hydrated during fasting periods (crucial for fasting comfort and safety), meet daily hydration goals, track how hydration patterns affect your fasting experience, provide reminders to drink water during eating and fasting windows, understand correlations between hydration and energy levels or weight fluctuations, and promote overall health through proper hydration.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Table: `water_logs`</p>
            <br />
            <hr />
            <br />
            <h6 id="17-mood-and-wellness-data">1.7 Mood and Wellness Data</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Mood ratings: Self-reported mood scores (typically 1-10 scale or Poor/Fair/Good/Excellent)</li>
              <li>Mood timestamps: Date and time of mood logging</li>
              <li>Wellness notes: Optional text notes describing how you feel, energy levels, hunger levels, or other subjective experiences</li>
              <li>Energy levels: Self-reported energy assessment during fasting and eating windows</li>
              <li>Mood trends: Historical mood data for pattern identification</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To help you track subjective wellness indicators alongside objective health metrics, understand psychological and emotional patterns during different fasting states (e.g., how you feel during hour 12 vs hour 18 of a fast), correlate mood with fasting adherence, nutrition quality, sleep quality, and physical activity, identify factors that positively or negatively affect your well-being, provide insights into the holistic impact of your fasting regimen on mental and emotional health, and help you optimize your fasting schedule for both physical and mental wellness.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Table: `mood_logs`</p>
            <br />
            <hr />
            <br />
            <h6 id="18-physical-activity-level-pal-classification">1.8 Physical Activity Level (PAL) Classification</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Physical Activity Level (PAL) selection: Your self-reported general activity level category:</li>
              <li><strong>Sedentary:</strong> Little to no exercise, desk job (PAL multiplier: 1.2)</li>
              <li><strong>Lightly Active:</strong> Light exercise 1-3 days/week (PAL multiplier: 1.375)</li>
              <li><strong>Moderately Active:</strong> Moderate exercise 3-5 days/week (PAL multiplier: 1.55)</li>
              <li><strong>Very Active:</strong> Hard exercise 6-7 days/week (PAL multiplier: 1.725)</li>
              <li><strong>Extremely Active:</strong> Very hard exercise, physical job, or training twice per day (PAL multiplier: 1.9)</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To calculate personalized Total Daily Energy Expenditure (TDEE = BMR × PAL multiplier) for accurate daily caloric need estimation, adjust nutritional recommendations based on your general lifestyle activity level beyond logged exercises, provide appropriate fasting recommendations considering your energy demands, and ensure safe and effective fasting tailored to your activity level.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Table: `user` (field: `pal`)</p>
            <br />
            <hr />
            <br />
            <h6 id="19-health-goals-and-preferences">1.9 Health Goals and Preferences</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Primary health goals: Selected from options such as:</li>
              <li>Weight loss / Fat loss</li>
              <li>Weight maintenance</li>
              <li>Muscle gain / Bulking</li>
              <li>Improved energy levels</li>
              <li>Better sleep quality</li>
              <li>Enhanced mental clarity</li>
              <li>General health and wellness</li>
              <li>Disease prevention or management</li>
              <li>Dietary preferences: User preferences for meal planning and tracking</li>
              <li>Risk assessment responses: Answers to health questionnaire during onboarding (e.g., Do you have diabetes? Are you pregnant? History of eating disorders?)</li>
              <li>User-adjusted goals: Manual adjustments to caloric or macronutrient targets</li>
              <li>Macro distribution preferences: Target percentages or grams for carbohydrates, protein, and fat</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To personalize all aspects of the app experience including caloric calculations, fasting plan recommendations, safety warnings, and insights, ensure appropriate safety warnings for extended fasts (especially fasts exceeding 24-48 hours), customize fasting recommendations based on whether you're trying to lose weight, maintain, or gain, provide tailored advice considering any health conditions you've disclosed, and ensure the app serves your specific health and wellness objectives effectively.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Tables: `user`, `config`</p>
            <br />
            <hr />
            <br />
            <h6 id="110-notification-and-reminder-preferences">1.10 Notification and Reminder Preferences</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Notification settings: Whether notifications are enabled or disabled globally</li>
              <li>Fasting reminder preferences:</li>
              <li>Fasting start reminders (enabled/disabled)</li>
              <li>Fasting end reminders (enabled/disabled)</li>
              <li>Eating window reminders (enabled/disabled)</li>
              <li>Milestone reminders during fasting (enabled/disabled, e.g., "You've reached 12 hours!")</li>
              <li>Custom reminders (enabled/disabled, time before events)</li>
              <li>Hydration reminders: Whether water intake reminders are enabled</li>
              <li>Weight tracking reminders: Whether weight log reminders are enabled</li>
              <li>Daily check-in reminders: Whether daily fasting/wellness check-in prompts are enabled</li>
              <li>Quiet hours: Do Not Disturb time range (e.g., 10 PM - 7 AM)</li>
              <li>Long fast safety warnings: Whether warnings for extended fasts (>48 hours) are enabled and threshold settings</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To provide personalized, non-intrusive reminders that support your fasting regimen, help you remember to log water, weight, meals, and other data, avoid disturbing you during sleep or work hours, ensure safety by warning about potentially risky extended fasts, and enhance your overall app experience through customization.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Table: `fasting_notification_prefs`</p>
            <br />
            <hr />
            <br />
            <h6 id="111-achievements-streaks-and-gamification-data">1.11 Achievements, Streaks, and Gamification Data</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Fasting streaks: Current consecutive fasting streak, longest streak achieved, total completed fasts</li>
              <li>Achievement unlocks: Badges and milestones you've achieved (e.g., "10-Day Streak", "100 Fasts Completed", "30-Day Challenge Winner")</li>
              <li>Achievement progress: Current progress toward locked achievements (e.g., 7/10 fasts completed for a badge)</li>
              <li>Challenge participation: Enrollment in and completion status of daily, weekly, or monthly challenges</li>
              <li>Challenge completion dates: When you completed specific challenges</li>
              <li>Leaderboard data: Anonymous ranking data if leaderboards are enabled (currently not implemented, future feature)</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To increase motivation and engagement through gamification elements, celebrate your achievements and progress, provide visual indicators of consistency and dedication, encourage continued healthy habits through positive reinforcement, and make the fasting journey more enjoyable and rewarding.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Tables: `fasting_streak`, `achievements`, `challenges`, `challenge_completions`</p>
            <br />
            <hr />
            <br />
            <h6 id="112-daily-tracking-summaries">1.12 Daily Tracking Summaries</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Daily summaries per date: Aggregated data for each tracked day including:</li>
              <li>Total calories consumed</li>
              <li>Total carbohydrates consumed (grams)</li>
              <li>Total protein consumed (grams)</li>
              <li>Total fat consumed (grams)</li>
              <li>Total calories burned from logged physical activities</li>
              <li>Net calorie balance (consumed - burned)</li>
              <li>Number of meals logged</li>
              <li>Number of fasting sessions</li>
              <li>Water intake total</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To provide quick daily overview insights, enable weekly and monthly summary reports, track trends over time (e.g., average daily calories for the past 7 days), simplify data analysis and visualization in charts and graphs, and help you understand long-term patterns in your health behaviors.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Table: `tracked_days`</p>
            <br />
            <hr />
            <br />
            <h5 id="2-personal-information-we-collect">2. Personal Information We Collect</h5>
            <br />
            <p>In addition to health data, FastingSense collects minimal personal information necessary for app functionality:</p>
            <br />
            <h6 id="21-demographic-information">2.1 Demographic Information</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>Age (calculated from date of birth)</li>
              <li>Gender (Male/Female)</li>
              <li>Height</li>
              <li>Current weight</li>
            </ul>
            <br />
            <p><strong>Why This is Required:</strong></p>
            <p>Age, gender, height, and weight are <strong>required</strong> to calculate your Basal Metabolic Rate (BMR) using the scientifically validated Mifflin-St Jeor equation. Without these data points, we cannot provide personalized caloric recommendations, which are core to the app's functionality.</p>
            <br />
            <p><strong>Name:</strong></p>
            <p>We do <strong>not</strong> collect your name. The app does not require or store any name, username, or identifying label. You remain completely anonymous.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Table: `user`</p>
            <br />
            <h6 id="22-app-preferences-and-settings">2.2 App Preferences and Settings</h6>
            <br />
            <p><strong>What We Collect:</strong></p>
            <ul>
              <li>App theme preference: Selected visual theme (Light, Dark, System Default, Custom glassmorphism themes)</li>
              <li>Unit system: Metric (kg, cm, ml) or Imperial (lbs, inches, oz)</li>
              <li>Language selection: Preferred app language (currently English, expandable)</li>
              <li>Disclaimer acceptance: Whether you've accepted the health disclaimer</li>
              <li>Privacy policy acceptance: Whether you've accepted this privacy policy</li>
              <li>Anonymous data opt-in: Whether you've agreed to share anonymous usage data (currently not implemented; if implemented, explicit opt-in required)</li>
            </ul>
            <br />
            <p><strong>Purpose:</strong></p>
            <p>To customize the app's appearance and behavior to your preferences, ensure appropriate unit conversions for international users, comply with legal requirements regarding disclaimers, and provide a personalized user experience.</p>
            <br />
            <p><strong>Storage:</strong></p>
            <p>Stored locally in encrypted SQLite database. Table: `config`</p>
            <br />
            <hr />
            <br />
            <h5 id="3-how-we-use-your-health-data">3. How We Use Your Health Data</h5>
            <br />
            <p>All health data collected by FastingSense is used <strong>exclusively on your device</strong> for the following purposes:</p>
            <br />
            <br />
            <h6 id="31-core-app-functionality">3.1 Core App Functionality</h6>
            <ul>
              <li>Display real-time fasting status, timer, and countdown to eating window or fast completion</li>
              <li>Calculate and display current metabolic zone (fat burning, ketosis, autophagy, deep ketosis)</li>
              <li>Calculate personalized caloric needs: BMR (Basal Metabolic Rate) using Mifflin-St Jeor equation and TDEE (Total Daily Energy Expenditure = BMR × PAL + exercise calories)</li>
              <li>Log and track meals, water intake, weight, sleep sessions, and physical activities</li>
              <li>Calculate and display daily nutritional intake (calories, macronutrients, micronutrients)</li>
              <li>Generate fasting statistics: average fast duration, completion rate, streak information</li>
              <li>Provide fasting reminders and notifications based on your preferences</li>
              <li>Create visual charts, graphs, and progress reports (weight trends, fasting history, calorie trends)</li>
              <li>Analyze correlations between sleep quality and fasting performance</li>
              <li>Track achievement progress and unlock badges</li>
              <li>Display daily, weekly, and monthly summaries of health metrics</li>
              <li>Provide the home dashboard with comprehensive health insights</li>
              <li>Enable data export in CSV or JSON format when you choose to export</li>
            </ul>
            <br />
            <h6 id="32-health-insights-and-analytics-on-device-only">3.2 Health Insights and Analytics (On-Device Only)</h6>
            <ul>
              <li>Analyze patterns in weight trends relative to caloric intake, fasting adherence, and physical activity</li>
              <li>Identify correlations between sleep quality and fasting success rates</li>
              <li>Calculate average fasting durations and recommend optimal fasting schedules</li>
              <li>Provide insights into macronutrient distribution and suggest adjustments</li>
              <li>Detect trends in mood or energy levels during different fasting states</li>
              <li>Generate weekly and monthly wellness summaries with actionable insights</li>
              <li>Highlight achievements and milestones to maintain motivation</li>
              <li>Identify days with highest/lowest caloric intake or activity levels</li>
            </ul>
            <br />
            <p><strong>Important Note:</strong> All analytics are performed <strong>locally on your device</strong>. We do not send your data to external servers for analysis or use cloud-based AI/ML models.</p>
            <br />
            <h6 id="33-personalization">3.3 Personalization</h6>
            <ul>
              <li>Customize fasting plan recommendations based on your health goals, activity level, and past performance</li>
              <li>Recommend appropriate fasting durations considering your BMI, age, and fitness level</li>
              <li>Adjust daily caloric targets dynamically as your weight changes</li>
              <li>Provide safety warnings for extended fasts (>48 hours) tailored to your health profile</li>
              <li>Suggest optimal eating window timing based on your sleep schedule and activity patterns</li>
              <li>Personalize notification timing and content based on your fasting habits</li>
              <li>Adapt achievement challenges to your progress and capabilities</li>
            </ul>
            <br />
            <h6 id="34-data-export-user-initiated-only">3.4 Data Export (User-Initiated Only)</h6>
            <ul>
              <li>Export all your health data in CSV or JSON format when you explicitly choose "Export Data" from Settings</li>
              <li>Enable data portability so you can use your data with other applications or keep personal backups</li>
              <li>Provide a complete snapshot of all tracked metrics for your personal records or sharing with healthcare providers</li>
            </ul>
            <br />
            <p><strong>What We Do NOT Use Your Data For:</strong></p>
            <p>❌ Advertising or marketing purposes</p>
            <p>❌ Selling or sharing with third parties for commercial gain</p>
            <p>❌ Training machine learning models or AI systems</p>
            <p>❌ User profiling for behavioral targeting</p>
            <p>❌ Analytics services that transmit your data externally (e.g., Google Analytics, Firebase Analytics)</p>
            <p>❌ Cross-app tracking or linking your identity across different apps</p>
            <p>❌ Building advertising profiles</p>
            <p>❌ Any purpose other than providing the core app functionality described above</p>
            <br />
            <hr />
            <br />
            <h5 id="4-data-storage-and-security">4. Data Storage and Security</h5>
            <br />
            <h6 id="41-local-storage-only">4.1 Local Storage Only</h6>
            <br />
            <p><strong>Primary Principle: Your Data Stays on Your Device</strong></p>
            <br />
            <ul>
              <li><strong>All health data is stored locally</strong> on your device in encrypted SQLite databases using Android's Room Persistence Library</li>
              <li><strong>No cloud storage:</strong> We do not operate servers that store, process, or have access to your personal or health information</li>
              <li><strong>No cloud synchronization:</strong> Your data is not synced across devices. Each device maintains its own independent local database</li>
              <li><strong>No account required:</strong> You do not need to create an account, provide an email address, phone number, or any identifying information to use FastingSense</li>
              <li><strong>Device-specific:</strong> Your data exists only on your device. If you switch devices, data does not automatically transfer (though you can manually export and import)</li>
              <li><strong>No remote access:</strong> We cannot access, view, modify, or retrieve your data remotely. Your data is entirely under your physical control</li>
            </ul>
            <br />
            <h6 id="42-data-encryption-and-protection">4.2 Data Encryption and Protection</h6>
            <br />
            <p><strong>Database Encryption:</strong></p>
            <ul>
              <li>All data is stored using Android's built-in Room Persistence Library with SQLite encryption</li>
              <li>The database is protected by Android's application sandboxing, preventing access from other apps</li>
              <li>Data is stored in the app's private directory: `/data/data/mwxapps.fastingsense.app/`</li>
            </ul>
            <br />
            <p><strong>File Storage:</strong></p>
            <ul>
              <li>Food images (meal photos) are stored in the app's private storage directory, accessible only to FastingSense</li>
              <li>Images are stored outside the database to optimize performance</li>
              <li>Image files are protected by Android's file system permissions</li>
            </ul>
            <br />
            <p><strong>Export Files:</strong></p>
            <ul>
              <li>When you export data, CSV/JSON files are temporarily created in your device's shared storage (Downloads or Documents folder)</li>
              <li>Export files are <strong>not encrypted</strong> by the app (they are plain text for compatibility)</li>
              <li><strong>User Responsibility:</strong> You should store exported files securely and delete them when no longer needed</li>
            </ul>
            <br />
            <p><strong>Network Security:</strong></p>
            <ul>
              <li>API calls to OpenFoodFacts and USDA FoodData Central use HTTPS encryption</li>
              <li>No sensitive personal or health data is transmitted in these API calls (only barcodes and search queries)</li>
            </ul>
            <br />
            <h6 id="43-data-retention">4.3 Data Retention</h6>
            <br />
            <p><strong>On-Device Retention:</strong></p>
            <ul>
              <li>Your data remains on your device indefinitely until you explicitly delete it</li>
              <li>No automatic data deletion or expiration</li>
              <li>Historical data is retained to enable long-term trend analysis and insights</li>
            </ul>
            <br />
            <p><strong>Data Deletion Options:</strong></p>
            <ul>
              <li><strong>Delete individual entries:</strong> Long-press any meal, weight log, fasting session, or other entry within the app to delete it</li>
              <li><strong>Delete all data:</strong> Go to Settings > Data Management > Delete All Data (requires confirmation)</li>
              <li><strong>Uninstall app:</strong> Uninstalling FastingSense permanently deletes all local data from your device (Android OS automatically removes app data)</li>
            </ul>
            <br />
            <p><strong>No Backup Copies:</strong></p>
            <ul>
              <li>We do not create or retain backup copies of your data on our servers</li>
              <li>We cannot recover your data if you delete it or uninstall the app</li>
              <li><strong>Recommendation:</strong> Use the Export feature regularly to create your own backups</li>
            </ul>
            <br />
            <h6 id="44-data-portability">4.4 Data Portability</h6>
            <br />
            <ul>
              <li>You can export all your data in standard CSV or JSON formats at any time</li>
              <li>Exported files are human-readable and compatible with spreadsheet applications (Excel, Google Sheets)</li>
              <li>JSON exports can be imported into other applications or used for custom analysis</li>
              <li>Export includes all tracked data: fasts, meals, weight, sleep, activities, water, mood, etc.</li>
            </ul>
            <br />
            <hr />
            <br />
            <h5 id="5-data-sharing-and-third-party-services">5. Data Sharing and Third-Party Services</h5>
            <br />
            <p>FastingSense <strong>does not share your personal or health data</strong> with any third parties for advertising, analytics, marketing, or commercial purposes. However, the app uses the following third-party services under specific circumstances:</p>
            <br />
            <h6 id="51-openfoodfacts-api-food-nutrition-database">5.1 OpenFoodFacts API (Food Nutrition Database)</h6>
            <br />
            <p><strong>Service:</strong> OpenFoodFacts (https://world.openfoodfacts.org/)</p>
            <p><strong>Purpose:</strong> When you scan a product barcode or search for food items, we send API requests to OpenFoodFacts' public database to retrieve nutritional information.</p>
            <br />
            <p><strong>Data Transmitted:</strong></p>
            <ul>
              <li><strong>Barcode numbers</strong> (UPC/EAN codes) when you scan a product</li>
              <li><strong>Search queries</strong> (e.g., "banana", "chicken breast") when you search for foods</li>
              <li><strong>No personal data:</strong> Your name, age, weight, health metrics, or any identifying information is NOT transmitted</li>
            </ul>
            <br />
            <p><strong>Data Received:</strong></p>
            <ul>
              <li>Product name, brand, nutritional values (calories, macros, micronutrients), product images</li>
            </ul>
            <br />
            <p><strong>Privacy Policy:</strong> https://world.openfoodfacts.org/privacy</p>
            <p><strong>User Control:</strong> You can use the app without barcode scanning or food search by manually entering meals. Scanning and searching are optional features.</p>
            <br />
            <p><strong>API Security:</strong> Requests use HTTPS encryption</p>
            <br />
            <hr />
            <br />
            <h6 id="52-usda-fooddata-central-api-optional-food-database">5.2 USDA FoodData Central API (Optional Food Database)</h6>
            <br />
            <p><strong>Service:</strong> U.S. Department of Agriculture FoodData Central (https://fdc.nal.usda.gov/)</p>
            <p><strong>Purpose:</strong> Optional enhanced food database for nutritional information lookup, particularly for whole foods and ingredients.</p>
            <br />
            <p><strong>Data Transmitted:</strong></p>
            <ul>
              <li><strong>Search queries only</strong> (e.g., "apple", "brown rice")</li>
              <li><strong>No personal data</strong> or health information</li>
            </ul>
            <br />
            <p><strong>Data Received:</strong></p>
            <ul>
              <li>Food descriptions, serving sizes, detailed nutritional information</li>
            </ul>
            <br />
            <p><strong>Privacy Policy:</strong> https://www.usda.gov/privacy-policy</p>
            <p><strong>User Control:</strong> This feature is optional and can be disabled. Food search defaults to OpenFoodFacts.</p>
            <br />
            <p><strong>API Security:</strong> Requests use HTTPS encryption</p>
            <br />
            <hr />
            <br />
            <h6 id="53-google-health-connect-optional-integration">5.3 Google Health Connect (Optional Integration)</h6>
            <br />
            <p><strong>Service:</strong> Google Health Connect (https://health.google/health-connect-android/)</p>
            <p><strong>Purpose:</strong> Optional integration to read sleep data from your connected fitness devices/wearables and write manually logged sleep data to Health Connect for a centralized health record.</p>
            <br />
            <p><strong>Data Shared:</strong></p>
            <ul>
              <li><strong>Sleep session data:</strong> Start time, end time, duration, sleep stages, quality ratings</li>
              <li><strong>Bidirectional sync:</strong> We READ sleep data from Health Connect (if you grant permission) and WRITE sleep data to Health Connect when you log sleep manually in our app</li>
            </ul>
            <br />
            <p><strong>Permissions Required:</strong></p>
            <ul>
              <li>`android.permission.health.READ_SLEEP` - To read sleep data from Health Connect and connected devices</li>
              <li>`android.permission.health.WRITE_SLEEP` - To write manually logged sleep data to Health Connect</li>
            </ul>
            <br />
            <p><strong>User Control:</strong></p>
            <ul>
              <li>This integration is <strong>entirely optional</strong></li>
              <li>You can deny Health Connect permissions and still use all sleep tracking features through manual entry in FastingSense</li>
              <li>Permissions are controlled by Android's Health Connect permission system</li>
              <li>You can revoke permissions at any time through:</li>
              <li>Device Settings > Apps > FastingSense > Permissions</li>
              <li>Health Connect app > Apps > FastingSense > Remove permissions</li>
            </ul>
            <br />
            <p><strong>Data Flow:</strong></p>
            <ol>
              <li><strong>Reading (if READ_SLEEP granted):</strong> FastingSense imports sleep records from Health Connect, which aggregates data from your fitness apps and wearables (e.g., Google Fit, Fitbit, Samsung Health, Garmin, etc.)</li>
              <li><strong>Writing (if WRITE_SLEEP granted):</strong> When you manually log sleep in FastingSense, we save it to Health Connect so all your health apps can access a unified sleep record</li>
            </ol>
            <br />
            <p><strong>Privacy Policy:</strong> https://support.google.com/healthconnect/answer/12966407</p>
            <br />
            <p><strong>Important Note:</strong> We do not transmit sleep data to our own servers. Health Connect sync occurs directly between your device's Health Connect system and FastingSense's local database. Health Connect is a Google service running on your device, not a cloud service.</p>
            <br />
            <hr />
            <br />
            <h6 id="54-what-we-do-not-use">5.4 What We Do NOT Use</h6>
            <br />
            <p>FastingSense <strong>does NOT use</strong> any of the following:</p>
            <br />
            <p>❌ <strong>Analytics Services:</strong></p>
            <ul>
              <li>No Google Analytics</li>
              <li>No Firebase Analytics</li>
              <li>No Mixpanel, Amplitude, or any usage analytics platforms</li>
              <li>No crash reporting services that transmit user data (e.g., Crashlytics with user IDs)</li>
            </ul>
            <br />
            <p>❌ <strong>Advertising Networks:</strong></p>
            <ul>
              <li>No Google Ads</li>
              <li>No Facebook Ads</li>
              <li>No ad networks of any kind</li>
              <li>No ad identifiers or tracking pixels</li>
            </ul>
            <br />
            <p>❌ <strong>Social Media Integrations:</strong></p>
            <ul>
              <li>No Facebook SDK</li>
              <li>No Twitter integration</li>
              <li>No social sharing that transmits user data</li>
              <li>No social media tracking</li>
            </ul>
            <br />
            <p>❌ <strong>Cloud Services (for user data):</strong></p>
            <ul>
              <li>No Firebase Realtime Database or Firestore</li>
              <li>No AWS cloud storage of user data</li>
              <li>No custom backend servers storing health data</li>
            </ul>
            <br />
            <p>❌ <strong>Tracking and Profiling:</strong></p>
            <ul>
              <li>No cookies or tracking cookies</li>
              <li>No cross-site tracking</li>
              <li>No user behavior profiling for commercial purposes</li>
              <li>No data brokers or data selling</li>
            </ul>
            <br />
            <p><strong>Result:</strong> Your usage patterns, health information, and personal data remain completely private.</p>
            <br />
            <hr />
            <br />
            <h5 id="6-permissions-requested">6. Permissions Requested</h5>
            <br />
            <p>FastingSense requests the following Android permissions. Below is a complete explanation of each permission, why it's needed, and whether it's optional:</p>
            <br />
            <h6 id="61-required-permissions-app-cannot-function-without-these">6.1 Required Permissions (App Cannot Function Without These)</h6>
            <br />
            <p><strong>1. INTERNET (`android.permission.INTERNET`)</strong></p>
            <ul>
              <li><strong>Purpose:</strong> To access OpenFoodFacts and USDA food databases when you search for foods or scan barcodes</li>
              <li><strong>What's transmitted:</strong> Only barcode numbers and food search queries</li>
              <li><strong>What's NOT transmitted:</strong> No personal data, health metrics, or identifying information</li>
              <li><strong>Can be denied:</strong> No, but the app will work offline for core fasting features if no internet is available</li>
            </ul>
            <br />
            <p><strong>2. ACCESS_NETWORK_STATE (`android.permission.ACCESS_NETWORK_STATE`)</strong></p>
            <ul>
              <li><strong>Purpose:</strong> To check if internet connection is available before attempting to query food databases</li>
              <li><strong>Avoids:</strong> Unnecessary API call failures and improves user experience</li>
              <li><strong>Can be denied:</strong> No (system permission, automatically granted)</li>
            </ul>
            <br />
            <p><strong>3. POST_NOTIFICATIONS (`android.permission.POST_NOTIFICATIONS`)</strong></p>
            <p><em>Note: Required on Android 13+ (API 33+)</em></p>
            <ul>
              <li><strong>Purpose:</strong> To send fasting reminders, eating window alerts, milestone notifications, hydration reminders, and weight tracking reminders</li>
              <li><strong>Can be denied:</strong> Yes, but you'll miss important reminders for your fasting schedule</li>
              <li><strong>User control:</strong> Can be revoked at any time through Settings > Apps > FastingSense > Notifications</li>
            </ul>
            <br />
            <hr />
            <br />
            <h6 id="62-optional-permissions-requested-only-when-needed">6.2 Optional Permissions (Requested Only When Needed)</h6>
            <br />
            <p><strong>4. CAMERA (`android.permission.CAMERA`)</strong></p>
            <ul>
              <li><strong>Purpose:</strong> To scan product barcodes for quick meal logging</li>
              <li><strong>When requested:</strong> Only when you tap the barcode scan button</li>
              <li><strong>Can be denied:</strong> Yes. You can manually enter meals instead of scanning barcodes</li>
              <li><strong>Alternative:</strong> Use the food search feature or manual meal entry</li>
              <li><strong>User control:</strong> Settings > Apps > FastingSense > Permissions > Camera</li>
            </ul>
            <br />
            <p><strong>5. SCHEDULE_EXACT_ALARM (`android.permission.SCHEDULE_EXACT_ALARM`)</strong></p>
            <p><em>Note: Required on Android 12+ (API 31+)</em></p>
            <ul>
              <li><strong>Purpose:</strong> To send precise fasting timer notifications at your specified times (e.g., exactly when your eating window opens)</li>
              <li><strong>Why needed:</strong> Without this, notifications may be delayed by several minutes</li>
              <li><strong>Can be denied:</strong> Yes, but notifications may not be as timely</li>
              <li><strong>User control:</strong> Settings > Apps > Special app access > Alarms & reminders</li>
            </ul>
            <br />
            <p><strong>6. RECEIVE_BOOT_COMPLETED (`android.permission.RECEIVE_BOOT_COMPLETED`)</strong></p>
            <ul>
              <li><strong>Purpose:</strong> To reschedule fasting reminders and notifications after your device restarts</li>
              <li><strong>Why needed:</strong> Android clears all scheduled alarms on reboot; this permission allows us to restore them</li>
              <li><strong>Can be denied:</strong> No (system permission, automatically granted)</li>
            </ul>
            <br />
            <p><strong>7. Health Connect Sleep Permissions</strong></p>
            <br />
            <p><strong>health.READ_SLEEP (`android.permission.health.READ_SLEEP`)</strong></p>
            <ul>
              <li><strong>Purpose:</strong> To read sleep data from Google Health Connect and your connected fitness devices/wearables</li>
              <li><strong>Enables:</strong> Automatic sleep tracking from smartwatches (Fitbit, Garmin, Samsung Galaxy Watch, etc.)</li>
              <li><strong>Can be denied:</strong> Yes. You can manually log sleep in FastingSense instead</li>
              <li><strong>User control:</strong> Health Connect app > Apps > FastingSense > Sleep permissions</li>
            </ul>
            <br />
            <p><strong>health.WRITE_SLEEP (`android.permission.health.WRITE_SLEEP`)</strong></p>
            <ul>
              <li><strong>Purpose:</strong> To write manually logged sleep data to Google Health Connect for a centralized health record across all your health apps</li>
              <li><strong>Enables:</strong> Unified sleep data viewable in Health Connect, Google Fit, and other connected apps</li>
              <li><strong>Can be denied:</strong> Yes. Your sleep logs will only be stored locally in FastingSense</li>
              <li><strong>User control:</strong> Health Connect app > Apps > FastingSense > Sleep permissions</li>
            </ul>
            <br />
            <hr />
            <br />
            <h6 id="63-permissions-we-do-not-request">6.3 Permissions We Do NOT Request</h6>
            <br />
            <p>FastingSense does NOT request any of the following:</p>
            <br />
            <p>❌ Location permissions (FINE_LOCATION, COARSE_LOCATION)</p>
            <p>❌ Contacts access</p>
            <p>❌ Phone call access</p>
            <p>❌ SMS/MMS access</p>
            <p>❌ Calendar access</p>
            <p>❌ Microphone access</p>
            <p>❌ Storage access (READ_EXTERNAL_STORAGE, WRITE_EXTERNAL_STORAGE) - We use scoped storage instead</p>
            <p>❌ Accounts access</p>
            <p>❌ Bluetooth permissions (unless needed for specific wearable integration in future versions)</p>
            <p>❌ Body sensors (BODY_SENSORS) - Sleep data comes through Health Connect, not direct sensor access</p>
            <br />
            <hr />
            <br />
            <h5 id="7-your-rights-and-data-control">7. Your Rights and Data Control</h5>
            <br />
            <p>You have complete control over your data and extensive rights regarding how it's used:</p>
            <br />
            <h6 id="71-right-to-access-your-data">7.1 Right to Access Your Data</h6>
            <br />
            <ul>
              <li><strong>View all data:</strong> You can access all your stored data within the app at any time</li>
              <li>Fasting history: View all past fasting sessions, durations, and completion status</li>
              <li>Meal logs: Browse all recorded meals with nutritional details</li>
              <li>Weight history: See complete weight log with graphs and trends</li>
              <li>Sleep records: Review all sleep sessions and quality ratings</li>
              <li>Activity logs: Access all logged physical activities</li>
              <li>Water intake: View hydration history</li>
              <li>Mood logs: Review all mood and wellness entries</li>
              <li><strong>Detailed statistics:</strong> Access comprehensive reports, charts, and analytics</li>
              <li><strong>No restrictions:</strong> There are no limits on how often you can view your data</li>
            </ul>
            <br />
            <h6 id="72-right-to-export-your-data-data-portability">7.2 Right to Export Your Data (Data Portability)</h6>
            <br />
            <ul>
              <li><strong>Export all data:</strong> Go to Settings > Data Management > Export Data</li>
              <li><strong>Format options:</strong></li>
              <li><strong>CSV format:</strong> Compatible with Microsoft Excel, Google Sheets, LibreOffice</li>
              <li><strong>JSON format:</strong> Structured data for developers or advanced users</li>
              <li><strong>What's included:</strong> Complete export of all tracked data across all categories</li>
              <li><strong>File location:</strong> Exported files are saved to your device's Downloads folder</li>
              <li><strong>Frequency:</strong> Export as often as you like; no restrictions</li>
            </ul>
            <br />
            <h6 id="73-right-to-delete-your-data">7.3 Right to Delete Your Data</h6>
            <br />
            <p><strong>Delete Individual Entries:</strong></p>
            <ul>
              <li>Long-press any entry (meal, weight log, fasting session, etc.) within the app</li>
              <li>Tap the delete/trash icon</li>
              <li>Confirm deletion</li>
              <li>Deletion is immediate and permanent</li>
            </ul>
            <br />
            <p><strong>Delete All Data:</strong></p>
            <ul>
              <li>Go to Settings > Data Management > Delete All Data</li>
              <li>Confirm your choice (requires double confirmation)</li>
              <li>All local data is permanently erased</li>
              <li><strong>Warning:</strong> This action cannot be undone, and we cannot recover your data</li>
            </ul>
            <br />
            <p><strong>Uninstall App:</strong></p>
            <ul>
              <li>Uninstalling FastingSense from your device automatically and permanently deletes all local data</li>
              <li>Android OS removes all app data when an app is uninstalled</li>
              <li>We do not retain any backup copies</li>
            </ul>
            <br />
            <p><strong>No Data Retained by Us:</strong></p>
            <ul>
              <li>We do not have copies of your data on our servers</li>
              <li>We cannot restore deleted data</li>
              <li><strong>Recommendation:</strong> Export your data regularly for personal backups</li>
            </ul>
            <br />
            <h6 id="74-right-to-revoke-permissions">7.4 Right to Revoke Permissions</h6>
            <br />
            <p>You can revoke any optional permission at any time:</p>
            <br />
            <p><strong>Camera Permission:</strong></p>
            <ul>
              <li>Settings > Apps > FastingSense > Permissions > Camera > Deny</li>
              <li>Effect: Barcode scanning disabled; use manual entry or search instead</li>
            </ul>
            <br />
            <p><strong>Notification Permission:</strong></p>
            <ul>
              <li>Settings > Apps > FastingSense > Notifications > Turn off</li>
              <li>Effect: No reminders or alerts</li>
            </ul>
            <br />
            <p><strong>Health Connect Permissions:</strong></p>
            <ul>
              <li>Health Connect app > Apps > FastingSense > Remove permissions</li>
              <li>Effect: No automatic sleep import; can still log manually</li>
            </ul>
            <br />
            <p><strong>Result:</strong> Revoking permissions does not delete your existing data, only disables specific features.</p>
            <br />
            <h6 id="75-right-to-anonymity-no-account-required">7.5 Right to Anonymity (No Account Required)</h6>
            <br />
            <ul>
              <li><strong>No registration:</strong> You do not need to create an account to use FastingSense</li>
              <li><strong>No email:</strong> We do not collect or store your email address</li>
              <li><strong>No phone number:</strong> We do not collect your phone number</li>
              <li><strong>No username:</strong> The app does not require a username or identifier</li>
              <li><strong>No authentication:</strong> No login, no password, no credentials</li>
              <li><strong>Complete anonymity:</strong> We cannot identify you as a user</li>
              <li><strong>No cross-device tracking:</strong> Each device installation is independent</li>
            </ul>
            <br />
            <h6 id="76-right-to-opt-out-of-data-collection-limited">7.6 Right to Opt-Out of Data Collection (Limited)</h6>
            <br />
            <p><strong>Core Health Data:</strong></p>
            <ul>
              <li>Fasting, meal, weight, and activity data are required for the app to function</li>
              <li>You cannot opt out of local storage of this data while using the app</li>
              <li><strong>Alternative:</strong> If you don't want to track a specific metric, simply don't log it</li>
            </ul>
            <br />
            <p><strong>Optional Features:</strong></p>
            <ul>
              <li><strong>Barcode scanning:</strong> Don't grant camera permission or don't use the feature</li>
              <li><strong>Health Connect:</strong> Don't grant sleep permissions</li>
              <li><strong>Mood logging:</strong> Don't use the mood tracking feature</li>
              <li><strong>Photo uploads:</strong> Don't take meal photos</li>
            </ul>
            <br />
            <hr />
            <br />
            <h5 id="8-childrens-privacy">8. Children's Privacy</h5>
            <br />
            <p>FastingSense is <strong>not intended for use by individuals under the age of 18</strong>. Intermittent fasting and caloric restriction may not be appropriate or safe for children and adolescents due to their ongoing growth and development needs.</p>
            <br />
            <br />
            <p><strong>Our Policy:</strong></p>
            <ul>
              <li>We do not knowingly collect personal information from children under 18</li>
              <li>We do not verify user age during onboarding (no account creation)</li>
              <li>We rely on app store age ratings to restrict access (App rated 18+ or appropriate age in each jurisdiction)</li>
            </ul>
            <br />
            <p><strong>For Parents and Guardians:</strong></p>
            <ul>
              <li>If you believe your child under 18 has used FastingSense and entered personal information, you can uninstall the app to permanently delete all local data</li>
              <li>We do not have access to or copies of user data, so we cannot manually delete specific user accounts (no accounts exist)</li>
              <li><strong>Medical Advice:</strong> Children and adolescents should consult a healthcare provider before engaging in any fasting regimen</li>
            </ul>
            <br />
            <p><strong>Age Requirement:</strong> By using this app, you confirm you are at least 18 years of age.</p>
            <br />
            <hr />
            <br />
            <h5 id="9-international-users-and-data-location">9. International Users and Data Location</h5>
            <br />
            <p>FastingSense uses local device storage only. Your data never leaves your device (except for optional Health Connect sync, which occurs locally on your device, or when you manually export data).</p>
            <br />
            <p><strong>Data Location:</strong></p>
            <ul>
              <li><strong>All data stored locally</strong> on your device</li>
              <li><strong>No international data transfers:</strong> Your data is not transmitted to or stored on servers in any country</li>
              <li><strong>No cross-border transfers:</strong> No data crosses international borders through our systems</li>
            </ul>
            <br />
            <p><strong>For Users in the European Economic Area (EEA), United Kingdom, or Regions with Data Protection Laws (e.g., GDPR):</strong></p>
            <br />
            <p><strong>We are not a data controller or processor</strong> in the traditional sense, as we do not collect, store, or process your data on external servers. Your data remains on your device under your physical control. However, we provide the following clarifications for compliance:</p>
            <br />
            <p><strong>GDPR Rights:</strong></p>
            <ul>
              <li><strong>Right to Access (Article 15):</strong> You can view all your data within the app</li>
              <li><strong>Right to Rectification (Article 16):</strong> You can edit any entry within the app</li>
              <li><strong>Right to Erasure (Article 17):</strong> You can delete individual entries or all data from Settings</li>
              <li><strong>Right to Data Portability (Article 20):</strong> You can export all data in CSV/JSON formats</li>
              <li><strong>Right to Object (Article 21):</strong> You can stop using the app and uninstall to delete all data</li>
              <li><strong>Right to Withdraw Consent:</strong> You can revoke permissions (camera, Health Connect) at any time</li>
            </ul>
            <br />
            <p><strong>Legal Basis for Processing:</strong></p>
            <ul>
              <li><strong>Consent (Article 6(1)(a)):</strong> By using the app and entering data, you consent to local processing as described in this policy</li>
              <li><strong>Contract Performance (Article 6(1)(b)):</strong> Processing is necessary to provide the fasting tracking services you requested</li>
              <li><strong>Legitimate Interest (Article 6(1)(f)):</strong> We have a legitimate interest in providing health tracking tools, and processing is conducted locally on your device</li>
            </ul>
            <br />
            <p><strong>Data Minimization (Article 5(1)(c)):</strong> We collect only data necessary for app functionality.</p>
            <br />
            <p><strong>Purpose Limitation (Article 5(1)(b)):</strong> Data is used only for the purposes disclosed in this policy (no secondary uses).</p>
            <br />
            <p><strong>No Automated Decision-Making (Article 22):</strong> The app does not make automated decisions with legal or significant effects on you.</p>
            <br />
            <hr />
            <br />
            <h5 id="10-changes-to-this-privacy-policy">10. Changes to This Privacy Policy</h5>
            <br />
            <p>We may update this Privacy Policy from time to time to reflect changes in:</p>
            <ul>
              <li>App features and functionality (e.g., new data types collected)</li>
              <li>Legal or regulatory requirements</li>
              <li>Privacy best practices</li>
            </ul>
            <br />
            <p><strong>How We Notify You:</strong></p>
            <ul>
              <li>The "Last Updated" date at the top of this policy will be revised</li>
              <li>Significant changes will be communicated via in-app notification upon app launch</li>
              <li>You will be prompted to review the updated policy before continuing to use the app</li>
              <li>The updated policy will be available at the privacy policy URL and within the app (Settings > Privacy Policy)</li>
            </ul>
            <br />
            <p><strong>Your Consent:</strong></p>
            <ul>
              <li>Continued use of the app after changes constitutes acceptance of the updated Privacy Policy</li>
              <li>If you do not agree with changes, you should stop using the app and delete all data</li>
            </ul>
            <br />
            <p><strong>Change Log (for transparency):</strong></p>
            <ul>
              <li><strong>Version 1.0 (January 20, 2026):</strong> Initial privacy policy</li>
              <li><strong>Version 2.0 (January 28, 2026):</strong> Comprehensive update with detailed data type disclosures, Health Connect integration details, and enhanced transparency for Google Play compliance</li>
            </ul>
            <br />
            <hr />
            <br />
            <h5 id="11-security-measures">11. Security Measures</h5>
            <br />
            <p>While we implement industry-standard security practices, please be aware of the following:</p>
            <br />
            <h6 id="111-our-security-measures">11.1 Our Security Measures</h6>
            <br />
            <p><strong>Application Security:</strong></p>
            <ul>
              <li>Data stored in encrypted SQLite databases using Android Room Persistence Library</li>
              <li>App protected by Android's application sandboxing (each app has isolated storage)</li>
              <li>Network communication (API calls) use HTTPS encryption with SSL/TLS</li>
              <li>No plaintext transmission of sensitive data</li>
              <li>Regular security updates through Google Play Store app updates</li>
            </ul>
            <br />
            <p><strong>Code Security:</strong></p>
            <ul>
              <li>Secure coding practices to prevent common vulnerabilities (SQL injection, XSS, etc.)</li>
              <li>Regular dependency updates to patch known vulnerabilities</li>
              <li>ProGuard/R8 code obfuscation to prevent reverse engineering</li>
            </ul>
            <br />
            <p><strong>No Cloud Infrastructure:</strong></p>
            <ul>
              <li>By not using cloud servers, we eliminate entire categories of security risks:</li>
              <li>No server breaches</li>
              <li>No database hacks</li>
              <li>No man-in-the-middle attacks on data sync</li>
              <li>No cloud provider vulnerabilities</li>
            </ul>
            <br />
            <h6 id="112-your-responsibility-device-security">11.2 Your Responsibility (Device Security)</h6>
            <br />
            <p><strong>Important:</strong> While we secure the app, you must secure your device:</p>
            <br />
            <p><strong>Device Lock Screen:</strong></p>
            <ul>
              <li>Use a strong PIN, password, pattern, or biometric authentication (fingerprint, face unlock)</li>
              <li>Enable automatic screen lock after a short timeout</li>
              <li><strong>Why:</strong> Anyone with physical access to your unlocked device can open FastingSense and view your data</li>
            </ul>
            <br />
            <p><strong>Device Encryption:</strong></p>
            <ul>
              <li>Enable full device encryption (enabled by default on modern Android devices)</li>
              <li><strong>Why:</strong> Protects data if your device is stolen</li>
            </ul>
            <br />
            <p><strong>App Permissions:</strong></p>
            <ul>
              <li>Review and revoke unnecessary permissions regularly</li>
              <li>Only grant permissions you're comfortable with</li>
            </ul>
            <br />
            <p><strong>Software Updates:</strong></p>
            <ul>
              <li>Keep your Android OS updated</li>
              <li>Keep FastingSense updated through Google Play Store</li>
              <li><strong>Why:</strong> Updates include security patches</li>
            </ul>
            <br />
            <p><strong>Secure Your Exports:</strong></p>
            <ul>
              <li>Exported CSV/JSON files are <strong>not encrypted</strong> by the app</li>
              <li>Store exported files in secure locations (encrypted folders, password-protected cloud storage)</li>
              <li>Delete exported files when no longer needed</li>
              <li><strong>Risk:</strong> Anyone with access to your Downloads folder can read exported data</li>
            </ul>
            <br />
            <p><strong>Lost or Stolen Devices:</strong></p>
            <ul>
              <li>If your device is lost or stolen, your data may be accessible to others if the device is not locked</li>
              <li>Use Android's "Find My Device" feature to remotely lock or erase your device</li>
              <li>We cannot remotely wipe your data (no cloud access)</li>
            </ul>
            <br />
            <hr />
            <br />
            <h5 id="12-medical-disclaimer">12. Medical Disclaimer</h5>
            <br />
            <p>FastingSense is a wellness tracking tool, <strong>not a medical device or medical advice service</strong>.</p>
            <br />
            <p><strong>What This App Is NOT:</strong></p>
            <ul>
              <li>❌ A medical device (not FDA-approved or regulated as such)</li>
              <li>❌ A substitute for professional medical advice, diagnosis, or treatment</li>
              <li>❌ A weight loss guarantee or health outcome promise</li>
              <li>❌ Suitable for everyone (see contraindications below)</li>
            </ul>
            <br />
            <p><strong>What This App IS:</strong></p>
            <ul>
              <li>✅ A self-tracking tool for intermittent fasting</li>
              <li>✅ A nutrition and calorie logging application</li>
              <li>✅ A wellness data aggregator (fasting, meals, sleep, activity, weight)</li>
              <li>✅ An informational and motivational aid</li>
            </ul>
            <br />
            <h6 id="121-consult-a-healthcare-provider">12.1 Consult a Healthcare Provider</h6>
            <br />
            <p><strong>IMPORTANT: You should consult a qualified healthcare provider before starting any fasting regimen, especially if you:</strong></p>
            <br />
            <p><strong>Medical Conditions:</strong></p>
            <ul>
              <li>Have diabetes (Type 1 or Type 2) or any metabolic disorder</li>
              <li>Have cardiovascular disease, high blood pressure, or heart conditions</li>
              <li>Have a history of eating disorders (anorexia, bulimia, binge eating disorder)</li>
              <li>Have kidney disease, liver disease, or any chronic illness</li>
              <li>Have a compromised immune system</li>
              <li>Have thyroid disorders or hormonal imbalances</li>
              <li>Have gout or high uric acid levels</li>
              <li>Have any other chronic medical condition</li>
            </ul>
            <br />
            <p><strong>Special Populations:</strong></p>
            <ul>
              <li>Are pregnant, trying to conceive, or breastfeeding</li>
              <li>Are under 18 years of age (children and adolescents should NOT use intermittent fasting)</li>
              <li>Are over 65 years of age (increased risk of malnutrition and muscle loss)</li>
              <li>Have low body weight or underweight BMI (&lt;18.5)</li>
              <li>Are an athlete or engage in high-intensity training</li>
            </ul>
            <br />
            <p><strong>Medications:</strong></p>
            <ul>
              <li>Take medications that require food intake (many medications must be taken with food)</li>
              <li>Take diabetes medications (insulin, metformin, sulfonylureas) - fasting can cause dangerous blood sugar drops</li>
              <li>Take blood pressure medications - fasting may affect blood pressure</li>
              <li>Take any prescription medications - fasting may affect medication efficacy or safety</li>
            </ul>
            <br />
            <p><strong>Other Considerations:</strong></p>
            <ul>
              <li>Have a job requiring high alertness (pilot, surgeon, driver) - fasting may cause fatigue or dizziness</li>
              <li>Are experiencing high stress, illness, or recovery from surgery</li>
            </ul>
            <br />
            <h6 id="122-potential-risks-of-fasting">12.2 Potential Risks of Fasting</h6>
            <br />
            <p>Intermittent fasting may cause:</p>
            <ul>
              <li>Low blood sugar (hypoglycemia), especially in diabetics</li>
              <li>Dehydration</li>
              <li>Nutrient deficiencies if not properly planned</li>
              <li>Fatigue, dizziness, weakness</li>
              <li>Irritability, mood changes</li>
              <li>Headaches</li>
              <li>Digestive issues</li>
              <li>Sleep disturbances</li>
              <li>Hormonal imbalances (especially in women)</li>
              <li>Muscle loss if protein intake is inadequate</li>
              <li>Triggering or worsening of eating disorders</li>
            </ul>
            <br />
            <p><strong>Extended Fasts (>24 hours):</strong></p>
            <ul>
              <li>Significantly increase the above risks</li>
              <li>Should only be attempted under medical supervision</li>
              <li>May cause electrolyte imbalances, which can be life-threatening</li>
              <li>FastingSense will display safety warnings for fasts exceeding 48 hours</li>
            </ul>
            <br />
            <h6 id="123-our-recommendations">12.3 Our Recommendations</h6>
            <br />
            <ul>
              <li><strong>Start slowly:</strong> Begin with shorter fasts (12-14 hours) and gradually increase if tolerated</li>
              <li><strong>Stay hydrated:</strong> Drink plenty of water, especially during fasting periods</li>
              <li><strong>Break your fast safely:</strong> Start with small, gentle foods; avoid large meals immediately after long fasts</li>
              <li><strong>Listen to your body:</strong> If you feel unwell, dizzy, or extremely weak, break your fast immediately</li>
              <li><strong>Eat nutritiously:</strong> During eating windows, consume balanced, nutrient-dense meals</li>
              <li><strong>Monitor your health:</strong> Track weight, energy levels, mood, and overall well-being</li>
              <li><strong>Stop if needed:</strong> Fasting is not for everyone; it's okay to stop if it doesn't work for you</li>
            </ul>
            <br />
            <h6 id="124-emergency-situations">12.4 Emergency Situations</h6>
            <br />
            <p><strong>Seek immediate medical attention if you experience:</strong></p>
            <ul>
              <li>Severe dizziness or fainting</li>
              <li>Chest pain or heart palpitations</li>
              <li>Difficulty breathing</li>
              <li>Severe abdominal pain</li>
              <li>Signs of dehydration (extreme thirst, dark urine, confusion)</li>
              <li>Blood sugar below 70 mg/dL (if diabetic)</li>
              <li>Any other concerning symptoms</li>
            </ul>
            <br />
            <p><strong>Break your fast immediately and seek medical care.</strong></p>
            <br />
            <h6 id="125-limitation-of-liability">12.5 Limitation of Liability</h6>
            <br />
            <p>To the maximum extent permitted by law:</p>
            <ul>
              <li>We are not liable for any health consequences resulting from your use of this app</li>
              <li>We make no warranties about the accuracy of caloric calculations, BMR/TDEE estimates, or nutritional data</li>
              <li>Nutritional data from OpenFoodFacts and USDA databases may contain errors; always verify critical information</li>
              <li>You use this app at your own risk</li>
              <li>We are not responsible for any weight gain, weight loss, health complications, or other outcomes</li>
            </ul>
            <br />
            <p><strong>By using FastingSense, you acknowledge and accept these risks and limitations.</strong></p>
            <br />
            <hr />
            <br />
            <h5 id="13-third-party-links-and-resources">13. Third-Party Links and Resources</h5>
            <br />
            <p>The app may contain links to third-party websites or resources for educational or informational purposes (e.g., links to OpenFoodFacts, USDA FoodData Central, Health Connect documentation, scientific articles about fasting).</p>
            <br />
            <p><strong>Disclaimer:</strong></p>
            <ul>
              <li>We are not responsible for the privacy practices, content, or accuracy of external websites</li>
              <li>Third-party sites have their own privacy policies and terms of service</li>
              <li>We do not endorse or guarantee the accuracy of third-party content</li>
              <li>Clicking external links is at your own discretion</li>
            </ul>
            <br />
            <p><strong>Recommendation:</strong> Review the privacy policies of any external sites you visit.</p>
            <br />
            <hr />
            <br />
            <h5 id="14-data-breach-notification">14. Data Breach Notification</h5>
            <br />
            <p><strong>No Cloud Data = No Cloud Breaches:</strong></p>
            <p>Because all your data is stored locally on your device and we do not maintain servers with user data, there is no risk of a cloud-based data breach affecting FastingSense users.</p>
            <br />
            <p><strong>Device-Level Security:</strong></p>
            <ul>
              <li>If your physical device is lost, stolen, or compromised, your data may be at risk</li>
              <li>This is a device security issue, not an app or server breach</li>
              <li>We cannot be notified of or mitigate device-level security incidents</li>
              <li><strong>Your responsibility:</strong> Secure your device with strong authentication and enable Find My Device</li>
            </ul>
            <br />
            <p><strong>App Vulnerabilities:</strong></p>
            <ul>
              <li>If a security vulnerability is discovered in the FastingSense app itself, we will:</li>
              <li>Release a security patch as quickly as possible</li>
              <li>Publish an update through Google Play Store</li>
              <li>Notify users via in-app notification (if applicable)</li>
              <li>Disclose the nature of the vulnerability after a patch is released</li>
            </ul>
            <br />
            <hr />
            <br />
            <h5 id="15-california-privacy-rights-ccpa-compliance">15. California Privacy Rights (CCPA Compliance)</h5>
            <br />
            <p>For California residents, the California Consumer Privacy Act (CCPA) provides additional rights:</p>
            <br />
            <h6 id="151-right-to-know">15.1 Right to Know</h6>
            <p><strong>What personal information do we collect?</strong></p>
            <p>See Section 1 (Health Data We Collect and Access) and Section 2 (Personal Information We Collect) for a comprehensive list.</p>
            <br />
            <p><strong>Can you see your data?</strong></p>
            <p>Yes, all data is viewable within the app at any time.</p>
            <br />
            <h6 id="152-right-to-delete">15.2 Right to Delete</h6>
            <p><strong>Can you delete your data?</strong></p>
            <p>Yes, you can delete individual entries or all data from Settings > Data Management > Delete All Data.</p>
            <br />
            <h6 id="153-right-to-opt-out-of-sale">15.3 Right to Opt-Out of Sale</h6>
            <p><strong>Do we sell your personal information?</strong></p>
            <p><strong>No.</strong> We do NOT sell your personal information to third parties, and we never will.</p>
            <br />
            <h6 id="154-right-to-non-discrimination">15.4 Right to Non-Discrimination</h6>
            <p><strong>Will we discriminate if you exercise your privacy rights?</strong></p>
            <p>No. We do not discriminate based on your exercise of CCPA rights.</p>
            <br />
            <h6 id="155-authorized-agent">15.5 Authorized Agent</h6>
            <p>If you wish to designate an authorized agent to exercise your rights on your behalf, please contact us at the email provided in Section 17.</p>
            <br />
            <hr />
            <br />
            <h5 id="16-additional-disclosures">16. Additional Disclosures</h5>
            <br />
            <h6 id="161-no-profiling-or-automated-decision-making">16.1 No Profiling or Automated Decision-Making</h6>
            <p>FastingSense does not:</p>
            <ul>
              <li>Create user profiles for marketing or commercial purposes</li>
              <li>Use algorithms to make automated decisions with legal or significant effects on you</li>
              <li>Employ AI or machine learning models that analyze your data for purposes other than app functionality</li>
            </ul>
            <br />
            <p>All calculations (BMR, TDEE, calorie targets) use standard scientific formulas, not opaque algorithms.</p>
            <br />
            <h6 id="162-open-source-and-transparency">16.2 Open Source and Transparency</h6>
            <p>While FastingSense is not fully open source, we are committed to transparency:</p>
            <ul>
              <li>This privacy policy documents all data collection and usage practices</li>
              <li>We provide detailed explanations of app functionality</li>
              <li>We maintain a privacy-first architecture with local-only data storage</li>
            </ul>
            <br />
            <h6 id="163-research-and-development">16.3 Research and Development</h6>
            <p>We do not use your data for research, academic studies, or product development purposes beyond improving FastingSense's core functionality.</p>
            <br />
            <p>If we ever introduce optional features that use aggregated, anonymized data for research, we will:</p>
            <ul>
              <li>Obtain explicit opt-in consent</li>
              <li>Clearly disclose the research purpose</li>
              <li>Ensure data is fully anonymized and cannot be re-identified</li>
              <li>Allow you to opt out at any time</li>
            </ul>
            <br />
            <p><strong>Current Status:</strong> No research use of user data. All data remains on your device.</p>
            <br />
            <hr />
            <br />
            <h5 id="17-contact-information">17. Contact Information</h5>
            <br />
            <p>If you have questions, concerns, requests, or feedback regarding this Privacy Policy or your data:</p>
            <br />
            <p><strong>Developer:</strong> MWX Apps</p>
            <p><strong>Email:</strong> support@mwxapps.com</p>
            <p><strong>App:</strong> FastingSense: Fasting Tracker</p>
            <p><strong>Package Name:</strong> mwxapps.fastingsense.app</p>
            <p><strong>Privacy Policy URL:</strong> https://mwx-apps.web.app/fastingsense/privacy-policy</p>
            <br />
            <p><strong>Response Time:</strong> We aim to respond to privacy inquiries within 30 days.</p>
            <br />
            <p><strong>For Data Requests:</strong></p>
            <ul>
              <li>Right to Access: View data within the app</li>
              <li>Right to Delete: Use in-app deletion features (Settings > Data Management)</li>
              <li>Right to Export: Use in-app export feature (Settings > Data Management > Export Data)</li>
            </ul>
            <br />
            <p><strong>For Privacy Concerns:</strong></p>
            <ul>
              <li>Email us at web-developer@outlook.com with detailed information about your concern</li>
              <li>We will investigate and respond as appropriate</li>
            </ul>
            <br />
            <p><strong>For Bugs or Security Vulnerabilities:</strong></p>
            <ul>
              <li>Report security issues to: support@mwxapps.com with subject line "SECURITY: [brief description]"</li>
              <li>We will prioritize security fixes and release patches promptly</li>
            </ul>
            <br />
            <hr />
            <br />
            <h5 id="18-consent-and-acknowledgment">18. Consent and Acknowledgment</h5>
            <br />
            <p>By installing, accessing, and using FastingSense, you acknowledge and agree that:</p>
            <br />
            <ol>
              <li>✅ You have read and understood this Privacy Policy in its entirety</li>
              <li>✅ You consent to the collection, use, and storage of your health data as described in this policy</li>
              <li>✅ You understand that all data is stored locally on your device</li>
              <li>✅ You are responsible for securing your device and exported data</li>
              <li>✅ You are at least 18 years of age</li>
              <li>✅ You have consulted with a healthcare provider if you have any medical concerns about fasting (or accept the risk of not doing so)</li>
              <li>✅ You understand this app is not medical advice and does not replace professional healthcare</li>
              <li>✅ You agree to the Medical Disclaimer and Limitation of Liability in Section 12</li>
              <li>✅ You understand your rights and how to exercise them (access, delete, export, revoke permissions)</li>
              <li>✅ You accept that we may update this policy and will notify you of significant changes</li>
            </ol>
            <br />
            <p><strong>If you do not agree with this Privacy Policy, do not use FastingSense.</strong></p>
            <br />
            <hr />
            <br />
            <h5 id="19-effective-date-and-revisions">19. Effective Date and Revisions</h5>
            <br />
            <p><strong>Effective Date:</strong> January 28, 2026</p>
            <p><strong>Last Updated:</strong> January 28, 2026</p>
            <p><strong>Version:</strong> 2.0 - Comprehensive Disclosure for Google Play Compliance</p>
            <br />
            <p><strong>Previous Versions:</strong></p>
            <ul>
              <li>Version 1.0 (January 20, 2026): Initial privacy policy</li>
            </ul>
            <br />
            <hr />
            <br />
            <h5 id="20-summary-table-data-collection-at-a-glance">20. Summary Table: Data Collection at a Glance</h5>
            <br />
            <p>For your convenience, here is a summary table of all data types:</p>
            <br />
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Data Type</th>
                    <th>Collected?</th>
                    <th>Storage Location</th>
                    <th>Shared with Third Parties?</th>
                    <th>Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Fasting sessions</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Track fasting progress</td>
                  </tr>
                  <tr>
                    <td><strong>Fasting plans</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Personalized fasting schedules</td>
                  </tr>
                  <tr>
                    <td><strong>Meals & nutrition</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No (barcode queries to OpenFoodFacts)</td>
                    <td>Track caloric/macro intake</td>
                  </tr>
                  <tr>
                    <td><strong>Food images</strong></td>
                    <td>✅ Yes (optional)</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Visual meal records</td>
                  </tr>
                  <tr>
                    <td><strong>Weight logs</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Track weight goals</td>
                  </tr>
                  <tr>
                    <td><strong>Height</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>BMR calculation</td>
                  </tr>
                  <tr>
                    <td><strong>Age/DOB</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>BMR calculation</td>
                  </tr>
                  <tr>
                    <td><strong>Gender</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>BMR calculation</td>
                  </tr>
                  <tr>
                    <td><strong>BMI</strong></td>
                    <td>✅ Yes (calculated)</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Health metrics</td>
                  </tr>
                  <tr>
                    <td><strong>Sleep data</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device + Health Connect (if enabled)</td>
                    <td>⚠️ Only with Health Connect (Google) if you grant permission</td>
                    <td>Analyze sleep-fasting correlation</td>
                  </tr>
                  <tr>
                    <td><strong>Sleep stages</strong></td>
                    <td>✅ Yes (if from Health Connect)</td>
                    <td>Local device + Health Connect</td>
                    <td>⚠️ Only with Health Connect</td>
                    <td>Detailed sleep analysis</td>
                  </tr>
                  <tr>
                    <td><strong>Physical activity</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Calculate TDEE</td>
                  </tr>
                  <tr>
                    <td><strong>Water intake</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Track hydration</td>
                  </tr>
                  <tr>
                    <td><strong>Mood logs</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Track wellness</td>
                  </tr>
                  <tr>
                    <td><strong>Physical Activity Level (PAL)</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Personalized calorie calculations</td>
                  </tr>
                  <tr>
                    <td><strong>Health goals</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Personalization</td>
                  </tr>
                  <tr>
                    <td><strong>Notification preferences</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Customized reminders</td>
                  </tr>
                  <tr>
                    <td><strong>Achievements/Streaks</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Motivation & gamification</td>
                  </tr>
                  <tr>
                    <td><strong>Daily summaries</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Reports & insights</td>
                  </tr>
                  <tr>
                    <td><strong>App preferences</strong></td>
                    <td>✅ Yes</td>
                    <td>Local device only</td>
                    <td>❌ No</td>
                    <td>Customization</td>
                  </tr>
                  <tr>
                    <td><strong>Barcode scans</strong></td>
                    <td>✅ Yes (barcode number sent to API)</td>
                    <td>Barcode numbers sent to OpenFoodFacts</td>
                    <td>⚠️ Barcode numbers only (no personal data)</td>
                    <td>Food lookup</td>
                  </tr>
                  <tr>
                    <td><strong>Food search queries</strong></td>
                    <td>✅ Yes (search terms sent to API)</td>
                    <td>Search terms sent to OpenFoodFacts/USDA</td>
                    <td>⚠️ Search terms only (no personal data)</td>
                    <td>Food lookup</td>
                  </tr>
                  <tr>
                    <td><strong>Name</strong></td>
                    <td>❌ No</td>
                    <td>N/A</td>
                    <td>N/A</td>
                    <td>N/A</td>
                  </tr>
                  <tr>
                    <td><strong>Email</strong></td>
                    <td>❌ No</td>
                    <td>N/A</td>
                    <td>N/A</td>
                    <td>N/A</td>
                  </tr>
                  <tr>
                    <td><strong>Phone number</strong></td>
                    <td>❌ No</td>
                    <td>N/A</td>
                    <td>N/A</td>
                    <td>N/A</td>
                  </tr>
                  <tr>
                    <td><strong>Device identifiers</strong></td>
                    <td>❌ No</td>
                    <td>N/A</td>
                    <td>N/A</td>
                    <td>N/A</td>
                  </tr>
                  <tr>
                    <td><strong>Location data</strong></td>
                    <td>❌ No</td>
                    <td>N/A</td>
                    <td>N/A</td>
                    <td>N/A</td>
                  </tr>
                  <tr>
                    <td><strong>Contact information</strong></td>
                    <td>❌ No</td>
                    <td>N/A</td>
                    <td>N/A</td>
                    <td>N/A</td>
                  </tr>
                  <tr>
                    <td><strong>Social media data</strong></td>
                    <td>❌ No</td>
                    <td>N/A</td>
                    <td>N/A</td>
                    <td>N/A</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <br />
            <p><strong>Legend:</strong></p>
            <ul>
              <li>✅ Yes: We collect this data</li>
              <li>❌ No: We do NOT collect this data</li>
              <li>⚠️ Limited: Shared only as described (e.g., barcode numbers, Health Connect sync)</li>
            </ul>
            <br />
            <hr />
            <br />
            <h5 id="final-notes">Final Notes</h5>
            <br />
            <p>This privacy policy is designed to provide complete transparency and comply with:</p>
            <ul>
              <li>Google Play Developer Program Policies</li>
              <li>Google Play User Data Policy</li>
              <li>Health Connect Integration Requirements</li>
              <li>GDPR (General Data Protection Regulation)</li>
              <li>CCPA (California Consumer Privacy Act)</li>
              <li>Android Permissions Best Practices</li>
            </ul>
            <br />
            <p><strong>Thank you for trusting FastingSense with your health data. Your privacy is our priority.</strong></p>
            <br />
            <hr />
            <br />
            <p><strong>Last Updated:</strong> January 28, 2026</p>
            <p><strong>Version:</strong> 2.0</p>
            <p><strong>Privacy Policy URL:</strong> https://mwx-apps.web.app/fastingsense/privacy-policy</p>
            <br />
            <hr />
            <br />
          </div>
        </div>
        <Footer />
      </div>
    </>
  )
}

export default FastingSensePolicy
